import { IRowData } from "@multiweb/types/contracts";
import { Control } from "UI/UI.BaseControls/Control";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { DataGrid } from "./DataGrid";
import { CompactDataRow } from "./DataGridSubcontrols/Components/CompactDataRow";
import { CompactHeaderRow } from "./DataGridSubcontrols/Components/CompactHeaderRow";
import { ICompactDataRowElement, RowClickEvent } from "./DataGridSubcontrols/Components/IDataRow";
import { EditableDBDataSource } from "./DataSource/EditableDBDataSource";

export class CompactDataGrid<R extends ICompactDataRowElement> extends DataGrid<R> {
    public compactAttributes: ICompactDataGridAttrInfo;

    public frozenColumCounts: number = 0;

    public isShowed: boolean;

    protected _headerDiv: HTMLElement;

    public set showFrameMenu(v: boolean) {
        this.headerRow.frameMenuVisibility = v;
    }

    public static isCompactGridShow(element: HTMLElement): boolean {
        return element.offsetWidth <= 450;
    }

    protected override _renderHeader(): void {
        this.classList.add(...this.headerRow.extraClasses);

        const pomDiv: HTMLDivElement = createElement({ css: { display: "table", width: "100%" } }) as HTMLDivElement;

        this._headerDiv = createElement({ classes: "header-div" });

        const headerTable = createElement({ type: "table", css: { overflow: "hidden" } }) as HTMLTableElement;
        const thead = createElement({ type: "thead", classes: "table-head" });
        headerTable.appendChild(thead);

        this._headerDiv.appendChild(pomDiv);

        pomDiv.appendChild(headerTable);

        this._mainListDiv.appendChild(this._headerDiv);

        thead.appendChild(this.headerRow);
    }

    protected override _constructor(): void {
        super._constructor();

        this.dataRows = new Array<R>();
        this.selectedRows = new Array<R>();
        this.headerRow = new CompactHeaderRow();

        this._dataRowFactory = () => new CompactDataRow() as any as R;
    }

    protected override _createComplexRow(rowData: IRowData): R {
        return this._createRow(rowData);
    }

    protected override async _attachDataGrid(): Promise<any> {
        await super._attachDataGrid();

        if (this.dataSource instanceof EditableDBDataSource) {
            const dataSource = this.dataSource;
            this._eventsController.add(dataSource.attributesChanged, (_sender, eventData) => {
                if (!this.visible)
                    return;

                const el = this._dataTable.querySelector(`:scope > [datakey='${eventData.key}']`) as CompactDataRow;

                if (el) {
                    for (const data of eventData.values) {
                        el.setData(data["0"], data["1"]);
                    }
                }
            });

            this._eventsController.add(dataSource.deleted, (_sender, payload) => {
                if (!this.visible) {
                    return;
                }

                const el = this._dataTable.querySelector(`:scope > [datakey='${payload.key}']`);

                if (el) {
                    el.remove();
                }
                this.removeRow(payload.key);
            });

            this._eventsController.add(dataSource.newCreated, async (_cancelableEventArgs, _sender, irow) => {
                if (!this.visible)
                    return;

                const newRow = this.insertNewRecord(irow);
                newRow.select();
            });

            this._eventsController.add(dataSource.saved, (_sender, irow) => {
                this.callEvent("Saved");
                if (!this.visible)
                    return;

                if (irow.key === irow.newKey)
                    return;

                const el = this._dataTable.querySelector(`:scope > [datakey='${irow.key}']`) as CompactDataRow;

                if (el)
                    el.dataKey = irow.newKey;
            });

            this._eventsController.add(dataSource.afterSaved, (_sender, _eventData) => {
                this.callEvent("AfterSaved");
            });
        }
    }

    /**
     * Vykresleni kontejneru pro data
     */
    protected override _renderDataContainer(): void {
        super._renderDataContainer();

        this._eventsController.addListener(this._listDiv, "scroll", (ev: UIEvent) => {
            this._scroll(ev, "normal");
        });
    }

    /**
     * Je zavoláno vždy po zobrazení DG pokud byl skrytý ať už styly nebo právama
     * @protected
     */
    protected override _recalculateAfterVisible(): void {
        // do nothing!
    }

    protected override _createRow(data: IRowData): R {
        const dataRow = this._dataRowFactory(this, data);
        dataRow.visibleAttrs = this.compactAttributes;
        dataRow.init(data, this, this._createRowControlElement(dataRow));
        this.initDataRowFlag(dataRow, data);

        this.dataRows.push(dataRow);
        if (this.isSelectedAll)
            dataRow.isSelected = true;

        if (dataRow instanceof Control) {
            dataRow.registerEventListener("rowclick", (e) => {
                this._rowClick(e as RowClickEvent, dataRow);
            });
        }

        return dataRow;
    }

    public override insertExistingRecord(dataRow: IRowData): R {
        return this.insertNewRecord(dataRow);
    }

    public override insertNewRecord(dataRow: IRowData): R {
        const beforeRow = this.selectedRows.length > 0 && this.dataRows && this.dataRows.length > 0 ? (this.dataRows.indexOf(this.selectedRows[0]) > -1 ? this.selectedRows[0] : undefined) : (this.dataRows.length > 0 ? this.dataRows[0] : undefined);
        const newRow = this._dataRowFactory(this, dataRow);
        newRow.visibleAttrs = this.compactAttributes;
        const dataSource = this.dataSource;
        newRow.init(dataRow, this, this._createRowControlElement(newRow));
        newRow.render();
        newRow.dataKey = dataRow[dataSource.dataKey].valueOf();

        this.emptyMessage.visible = false;

        if (beforeRow)
            this._dataTable.insertBefore(newRow as HTMLElement, beforeRow as HTMLElement);
        else
            this._dataTable.appendChild(newRow as HTMLElement);

        if (beforeRow) {
            const addIndex = this.dataRows.indexOf(beforeRow);
            this.dataRows.splice(addIndex, 0, newRow); //.push(newRow);
        } else {
            this.dataRows.push(newRow);
        }

        if (newRow instanceof Control) {
            newRow.registerEventListener("rowclick", (e) => {
                this._rowClick(e as RowClickEvent, newRow);
            });
        }

        return newRow;
    }
}

export interface ICompactDataGridAttrInfo {
    topLeft: string[];
    topRight: string[];
    bottom: string[];
}

registerCustomElement("tsc-CompactDataGrid", CompactDataGrid);
